import { Button, Stack, styled } from '@mui/material';
import defaultImportArtworkUrl from '@resources/img/create/design-lab-v4/import-thumbnails/snapback-with-printed-patch_start-blank.svg?url';
import defaultStartBlankArtworkUrl from '@resources/img/create/design-lab-v4/import-thumbnails/snapback-with-printed-patch_import.svg?url';
import { useCallback, useState } from 'react';
import Dialog from '../../Common/Dialog/Dialog';
import DialogContent from '../../Common/Dialog/DialogContent';
import DialogTitle from '../../Common/Dialog/DialogTitle';

// The supplied filenames are visually inverted: the pink artwork-card image is
// the import state in the approved layout, while the blue plus image is blank.
const PreviewStage = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '320px',
  height: '280px',
  margin: '0 auto 0',

  [theme.breakpoints.down('sm')]: {
    height: '210px',
  },
}));

const PreviewImage = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  display: 'block',
  width: '100%',
  height: '280px',
  objectFit: 'contain',
  opacity: 0,
  transition: 'opacity 160ms ease-out',

  '&.active': {
    opacity: 1,
  },

  [theme.breakpoints.down('sm')]: {
    top: 0,
    height: '100%',
  },
}));

interface Props {
  open: boolean
  title: string
  selectExistingLabel: string
  startBlankLabel: string
  importArtworkUrl?: string
  startBlankArtworkUrl?: string
  onClose: () => void
  onSelectExisting: () => void
  onStartBlank: () => void
}

type Preview = 'import' | 'start-blank'

export default function ImportDesignDialog(props: Props) {
  const [preview, setPreview] = useState<Preview>('import');
  const showImportPreview = useCallback(() => setPreview('import'), []);
  const showStartBlankPreview = useCallback(() => setPreview('start-blank'), []);
  const importArtworkUrl = props.importArtworkUrl ?? defaultImportArtworkUrl;
  const startBlankArtworkUrl = props.startBlankArtworkUrl ?? defaultStartBlankArtworkUrl;

  return <Dialog
    open={props.open}
    showCloseButton
    maxWidth="sm"
    fullWidth
    ornaments="shapes-3"
    onClose={props.onClose}
  >
    <DialogTitle
      sx={{
        pt: {
          xs: 3,
          sm: 5,
        },
        pb: 0,
        pr: '60px',
        pl: '60px',
        textAlign: 'center',
        textTransform: 'uppercase',
        fontSize: {
          xs: '2rem',
          sm: '2.5rem',
        },
      }}
    >{props.title}</DialogTitle>
    <DialogContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mt: 0,
        px: {
          xs: 3,
          sm: 5,
        },
        pt: {
          xs: 1,
          sm: 0,
        },
        pb: {
          xs: 4,
          sm: 5,
        },
      }}
    >
      <PreviewStage data-preview={preview}>
        <PreviewImage
          className={preview === 'import' ? 'active' : undefined}
          src={importArtworkUrl}
          alt=""
          aria-hidden="true"
        />
        <PreviewImage
          className={preview === 'start-blank' ? 'active' : undefined}
          src={startBlankArtworkUrl}
          alt=""
          aria-hidden="true"
        />
      </PreviewStage>
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        spacing={1.25}
        sx={{
          width: '100%',
          maxWidth: {
            xs: '320px',
            sm: '500px',
          },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Button
          variant="contained"
          color="accent"
          size="large"
          fullWidth
          onMouseEnter={showImportPreview}
          onFocus={showImportPreview}
          onClick={props.onSelectExisting}
        >{props.selectExistingLabel}</Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onMouseEnter={showStartBlankPreview}
          onFocus={showStartBlankPreview}
          onClick={props.onStartBlank}
        >{props.startBlankLabel}</Button>
      </Stack>
    </DialogContent>
  </Dialog>
}
